import { Container, Rectangle, Sprite, Texture } from "pixi.js";
import { PianoLayout } from "@/lib/piano/piano-layout";
import { PianoComponent } from "@/lib/studio/view/components/piano-component";
import type { LaneStyle } from "./notes";
import type { PedalTrail } from "./pedal-span";

export type PedalTrailsProps = {
  /** Les notes qui sonnent encore, déjà bornées par l'extinction des cordes. */
  trails: readonly PedalTrail[];
  laneColors: readonly LaneStyle[];
  fromTick: number;
  toTick: number;
  height: number;
  noteGap: number;
  /** La tête de lecture, quand les notes s'y arrêtent pour partir en paillettes. */
  clipY?: number;
};

/**
 * La traîne : ce qui sonne encore après que le doigt s'est levé.
 *
 * # Le mensonge qu'elle corrige
 *
 * Une note du roll dure exactement ce que la touche est restée enfoncée. Sous
 * pédale c'est faux : l'étouffoir reste levé, la corde continue, et le
 * rectangle s'arrête pourtant. Le roll montrait alors un silence là où il y a
 * du son — et c'est justement le passage où un pianiste a besoin de voir ce
 * qu'il laisse sonner.
 *
 * La traîne prolonge donc la note de sa fin écrite jusqu'à la levée de la
 * pédale de sa lane. Elle descend avec tout le reste : au-dessus de la tête
 * elle annonce ce qui va continuer, à la tête c'est le son qui dure, en
 * dessous le voile du passé la reprend.
 *
 * # Aussi large que sa note, et qui s'éteint
 *
 * Aussi large, parce que c'est la même corde : la traîne est la note qui
 * continue, pas un objet à côté d'elle, et la rétrécir en faisait une seconde
 * voix plus fine sous la première.
 *
 * Et elle pâlit sur sa longueur, du doigt qui se lève jusqu'à la pédale qui
 * se relève. C'est ce que fait le son — une corde libre décroît, elle ne tient
 * pas un plateau —, et c'est aussi ce qui la sépare d'une note : une note a
 * des bords nets parce qu'elle commence et finit, une résonance s'efface.
 * Elle part déjà très pâle : un morceau pédalé en porte une sous presque
 * chaque note, et à pleine opacité la page devient une forêt où plus personne
 * ne lit le jeu.
 *
 * # Elle s'arrête au premier des deux
 *
 * La pédale se relève, ou la corde s'éteint. Une corde libre ne sonne pas
 * indéfiniment — 25 s dans le grave, 4 s dans l'aigu sur les échantillons que
 * le studio joue —, et une traîne qui traverserait tout l'écran depuis un do7
 * dessinerait un son que personne n'entend. Voir `ringSeconds`, qui borne, et
 * `trailEnd`, qui dit lequel des deux a gagné : une corde éteinte s'efface,
 * un étouffoir coupe net.
 *
 * # Ce qu'elle ne fait pas
 *
 * Elle ne dessine rien pour une touche encore enfoncée : le corps de la note
 * monte déjà jusqu'à la tête de lecture, et la doubler n'ajouterait rien.
 *
 * Et elle vit dans sa propre passe, hors du cache de géométries des notes
 * (`note.ts`) : sa hauteur change à chaque image et pour chaque note, ce qui
 * est exactement ce qu'un cache par forme ne supporte pas.
 */
export class PedalTrails {
  private readonly container = new Container();
  /** Sprites réutilisés d'une image à l'autre, comme le fait `Notes`. */
  private readonly pool: Sprite[] = [];
  private used = 0;

  constructor(
    stage: Container,
    private readonly layout: PianoLayout,
    private readonly pianoComponent: PianoComponent,
  ) {
    stage.addChild(this.container);
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }

  private acquire(): Sprite {
    if (this.used < this.pool.length) {
      const sprite = this.pool[this.used++];
      sprite.visible = true;

      return sprite;
    }

    const sprite = new Sprite(Texture.EMPTY);
    sprite.visible = true;
    this.container.addChild(sprite);
    this.pool.push(sprite);
    this.used++;

    return sprite;
  }

  draw(props: PedalTrailsProps): void {
    const { trails, laneColors, fromTick, toTick, height, noteGap, clipY } = props;

    this.used = 0;

    if (trails.length === 0) {
      this.hideRest();

      return;
    }

    const pxPerTick = height / (toTick - fromTick);
    const projection = this.pianoComponent.getProjection();

    for (const trail of trails) {
      const yTop = height - (trail.toTick - fromTick) * pxPerTick;
      let yBottom = height - (trail.fromTick - fromTick) * pxPerTick;

      if (clipY !== undefined) {
        if (yTop >= clipY) {
          continue;
        }

        yBottom = Math.min(yBottom, clipY);
      }

      if (yBottom - yTop < MIN_HEIGHT_PX) {
        continue;
      }

      const keyLayout = this.layout.getKeyByMidi(trail.midi);

      if (!keyLayout) {
        continue;
      }

      const sprite = this.acquire();

      sprite.texture = trailTexture(trail.covered);
      sprite.x = projection.keyLeft(keyLayout) + noteGap;
      sprite.y = yTop;
      sprite.width = Math.max(1, projection.keyWidth(keyLayout) - noteGap * 2);
      sprite.height = yBottom - yTop;
      sprite.tint = laneColors[trail.laneIdx]?.fill ?? 0xffffff;
      sprite.alpha = ALPHA;
    }

    this.hideRest();
  }

  /** Les sprites que cette image n'a pas repris attendent, invisibles. */
  private hideRest(): void {
    for (let i = this.used; i < this.pool.length; i++) {
      this.pool[i].visible = false;
    }
  }
}

/**
 * La texture d'une traîne : blanche, pour que la teinte du sprite lui donne la
 * couleur de sa lane, et dégradée sur la hauteur.
 *
 * La rampe couvre **l'extinction entière** de la corde, pas la traîne : une
 * traîne coupée par la pédale n'en montre que le bas, et s'arrête donc sur une
 * tranche encore franche — l'étouffoir tombe sur un son qui vivait. C'est à ça
 * que sert `covered`, et c'est pour ça que la rampe est découpée en tranches
 * plutôt qu'étirée : une rampe étirée aurait fait s'éteindre proprement toutes
 * les traînes, y compris celles qu'on coupe.
 *
 * Une seule colonne suffit, l'étirement horizontal du sprite fait le reste, et
 * la rampe est plus creuse qu'une droite : une corde libre tombe vite puis
 * s'attarde.
 *
 * Cuite à la première image plutôt qu'au chargement du module : il n'y a pas
 * de `document` au rendu serveur.
 */
let ramp: Texture | null = null;
const slices = new Map<number, Texture>();

function trailTexture(covered: number): Texture {
  const bucket = Math.max(1, Math.min(BUCKETS, Math.ceil(covered * BUCKETS)));
  const known = slices.get(bucket);

  if (known !== undefined) {
    return known;
  }

  const full = rampTexture();
  const share = bucket / BUCKETS;
  const height = Math.max(1, Math.round(TEXTURE_HEIGHT * share));
  const slice = new Texture({
    source: full.source,
    frame: new Rectangle(0, TEXTURE_HEIGHT - height, 1, height),
  });

  slices.set(bucket, slice);

  return slice;
}

function rampTexture(): Texture {
  if (ramp !== null) {
    return ramp;
  }

  const canvas = document.createElement("canvas");

  canvas.width = 1;
  canvas.height = TEXTURE_HEIGHT;

  const ctx = canvas.getContext("2d");

  if (ctx === null) {
    return Texture.WHITE;
  }

  // De bas en haut : le bas est l'attaque perdue, le haut la corde éteinte.
  const gradient = ctx.createLinearGradient(0, TEXTURE_HEIGHT, 0, 0);

  for (const [at, alpha] of STOPS) {
    gradient.addColorStop(at, `rgba(255, 255, 255, ${alpha})`);
  }

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1, TEXTURE_HEIGHT);

  ramp = Texture.from(canvas);

  return ramp;
}

const TEXTURE_HEIGHT = 128;
/** Tranches d'extinction distinctes ; huit suffisent à l'œil et tiennent en cache. */
const BUCKETS = 8;
const STOPS: readonly (readonly [number, number])[] = [
  [0, 1],
  [0.3, 0.5],
  [0.7, 0.16],
  [1, 0],
];
const ALPHA = 0.06;
/** Sous cette hauteur, la traîne est un trait de bruit : elle n'est pas dessinée. */
const MIN_HEIGHT_PX = 1.5;
